import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, initials, fmtDate } from "@/components/common";
import DataTable from "@/components/DataTable";
import AdvancedFilters from "@/components/AdvancedFilters";
import QuickFilters from "@/components/QuickFilters";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { usePaged, useDebounced } from "@/lib/paged";
import { selectColumn, useIsAdmin, AdminDeleteBar } from "@/components/BulkEdit";

const QF = [{ key: "city", label: "Cities" }, { key: "state", label: "States" }, { key: "language", label: "Languages" }, { key: "gender", label: "Genders" }, { key: "source", label: "Sources" }, { key: "course", label: "Courses", get: (r) => (r.course || "").split(", ") }, { key: "colleges", label: "Colleges" }, { key: "last_disposition", label: "Last Dispositions" }];

export default function Students() {
  const navigate = useNavigate();
  const [quick, setQuick] = useState(null);
  const [display, setDisplay] = useState(null);
  const [search, setSearch] = useState("");
  const dq = useDebounced(search);
  const { rows, reload: load, complete } = usePaged("/students", { search: dq.trim() });
  const isAdmin = useIsAdmin();
  const [sel, setSel] = useState([]);
  const [pageView, setPageView] = useState([]);
  const FIELDS = useMemo(() => [
    { key: "name", label: "Name", type: "text" }, { key: "mobile", label: "Mobile", type: "text" }, { key: "email", label: "Email", type: "text" },
    { key: "city", label: "City", type: "text" }, { key: "state", label: "State", type: "text" }, { key: "language", label: "Language", type: "text" },
    { key: "twelfth_pct", label: "12th %", type: "number" }, { key: "created_at", label: "Created On", type: "date" },
  ], []);
  const columns = [
    selectColumn(pageView, sel, setSel, isAdmin),
    { key: "name", label: "Student", fixed: true, render: (r) => <div className="flex items-center gap-2"><div className="w-8 h-8 rounded-lg bg-[#312E81] text-white flex items-center justify-center text-[10px] font-bold">{initials(r.name)}</div><div><div className="font-semibold text-slate-800">{r.name}</div><div className="text-[11px] text-slate-400 font-mono">{r.mobile}</div></div></div> },
    { key: "email", label: "Email", render: (r) => <span className="text-xs">{r.email || "—"}</span> },
    { key: "city", label: "City" },
    { key: "state", label: "State" },
    { key: "twelfth_pct", label: "12th %", render: (r) => r.twelfth_pct || "—" },
    { key: "language", label: "Language" },
    { key: "gender", label: "Gender", hidden: true },
    { key: "parent_mobile", label: "Parent Mobile", hidden: true },
    { key: "created_at", label: "Created On", hidden: true, render: (r) => <span className="text-xs">{fmtDate(r.created_at)}</span> },
  ];
  return (
    <div>
      <PageHeader title="Students" subtitle="Master student directory (the person, across all enquiries)" testid="students-header" />
      <div className="relative max-w-xs mb-3">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search all students: name / mobile / email" className="pl-9 bg-white" data-testid="students-search" />
      </div>
      <QuickFilters rows={rows} fields={QF} onFiltered={setQuick} testid="students" />
      <AdvancedFilters entity="students" fields={FIELDS} rows={quick} onFiltered={setDisplay} />
      <AdminDeleteBar path="/students/bulk-delete" label="students" sel={sel} setSel={setSel} onDone={load} note="Each student is removed with their leads, follow-ups, tasks, counselling, applications and calls. Students who already have an admission are skipped." />
      <DataTable columns={columns} rows={display} loading={rows === null} testid="students-table" rowTestid="student-row" columnsKey="students" paginate loadingMore={!complete} onPageRows={(r) => { setPageView(r); setSel([]); }} onRowClick={(r) => navigate(`/students/${r.id}`)} />
    </div>
  );
}
